import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { authHeaders } from '../../services/auth';
import { ShieldCheck, Download, AlertCircle, RefreshCw, Mail, Phone, User as UserIcon, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const TCCompliancePanel = () => {
  const [summary, setSummary] = useState(null);
  const [pending, setPending] = useState([]);
  const [contact, setContact] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, c] = await Promise.all([
        fetch(`${API}/api/tc/management/summary`, { headers: authHeaders(), credentials: 'include' }),
        fetch(`${API}/api/tc/management/pending`, { headers: authHeaders(), credentials: 'include' }),
        fetch(`${API}/api/tc/grievance-contact`, { headers: authHeaders(), credentials: 'include' }),
      ]);
      if (s.ok) setSummary(await s.json());
      if (p.ok) {
        const j = await p.json();
        setPending(j.pending || []);
      }
      if (c.ok) {
        const j = await c.json();
        setContact(j.contact);
        setForm({
          name: j.contact?.name || '',
          email: j.contact?.email || '',
          phone: j.contact?.phone || '',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadAudit = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/tc/management/audit.csv`, {
        headers: authHeaders(),
        credentials: 'include',
      });
      if (!res.ok) { toast.error('Download failed'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tc_audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit log downloaded');
    } finally {
      setDownloading(false);
    }
  };

  const saveContact = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('All fields required'); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/tc/management/grievance-contact`, {
        method: 'PUT',
        credentials: 'include',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() }),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); toast.error(j.detail || 'Save failed'); return; }
      toast.success('Grievance contact updated');
      setEditing(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (loading && !summary) {
    return (
      <Card data-testid="tc-compliance-loading"><CardContent className="p-6 text-sm text-slate-500">Loading T&C compliance…</CardContent></Card>
    );
  }

  const pct = summary?.acceptance_pct ?? 0;
  const accColor = pct >= 90 ? 'text-emerald-700' : pct >= 70 ? 'text-amber-700' : 'text-red-700';

  return (
    <div className="space-y-4" data-testid="tc-compliance-panel">
      <Card className="shadow-md border-0">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-gk-teal" />
            T&C & Consent Compliance
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={load} data-testid="tc-refresh-button" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              onClick={downloadAudit}
              disabled={downloading}
              data-testid="tc-audit-csv-button"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              Audit CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div className="text-xs uppercase tracking-wide text-slate-500">Overall acceptance</div>
              <div className={`text-2xl font-bold mt-1 ${accColor}`} data-testid="tc-overall-pct">{pct}%</div>
              <div className="text-xs text-slate-500 mt-0.5">{summary?.accepted}/{summary?.total_users} users</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div className="text-xs uppercase tracking-wide text-slate-500">Resident acceptance</div>
              <div className="text-2xl font-bold mt-1 text-slate-900" data-testid="tc-resident-pct">{summary?.resident_acceptance_pct}%</div>
              <div className="text-xs text-slate-500 mt-0.5">{summary?.resident_accepted}/{summary?.total_residents} residents</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div className="text-xs uppercase tracking-wide text-slate-500">Pending</div>
              <div className="text-2xl font-bold mt-1 text-amber-700" data-testid="tc-pending-count">{summary?.pending}</div>
              <div className="text-xs text-slate-500 mt-0.5">awaiting consent</div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
              <div className="text-xs uppercase tracking-wide text-slate-500">T&C Version</div>
              <div className="text-2xl font-bold mt-1 text-slate-900">v{summary?.tc_version}</div>
              <div className="text-xs text-slate-500 mt-0.5">currently in force</div>
            </div>
          </div>

          {pending.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-600" /> Users yet to accept ({pending.length})
              </div>
              <div className="border border-slate-200 rounded-lg max-h-60 overflow-y-auto" data-testid="tc-pending-list">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-medium text-slate-600">Villa</th>
                      <th className="px-3 py-2 font-medium text-slate-600">Name</th>
                      <th className="px-3 py-2 font-medium text-slate-600">Role</th>
                      <th className="px-3 py-2 font-medium text-slate-600">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((u) => (
                      <tr key={u.id} className="border-t border-slate-100" data-testid={`tc-pending-row-${u.id}`}>
                        <td className="px-3 py-2 font-mono text-slate-800">{u.villa_number || '—'}</td>
                        <td className="px-3 py-2 text-slate-900">{u.name}</td>
                        <td className="px-3 py-2 text-slate-600 capitalize">{u.role}</td>
                        <td className="px-3 py-2 text-slate-600">{u.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-md border-0">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-slate-700" /> Grievance & Privacy Contact
          </CardTitle>
          {!editing && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="grievance-edit-button">
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {!editing ? (
            <div className="space-y-1.5 text-sm" data-testid="grievance-contact-display">
              <div className="flex items-center gap-2"><UserIcon className="w-4 h-4 text-slate-400" /> {contact?.name}</div>
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /> {contact?.email}</div>
              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /> {contact?.phone}</div>
              {contact?.source === 'global' && (
                <div className="text-xs text-amber-700 mt-2">
                  Currently using the platform default. Click <strong>Edit</strong> to set a community-specific contact (recommended).
                </div>
              )}
              {contact?.source === 'community' && (
                <div className="text-xs text-emerald-700 mt-2">Community-specific contact configured.</div>
              )}
            </div>
          ) : (
            <div className="space-y-3 max-w-md">
              <div>
                <Label className="text-xs">Name</Label>
                <Input
                  data-testid="grievance-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  data-testid="grievance-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input
                  data-testid="grievance-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button onClick={saveContact} disabled={saving} data-testid="grievance-save-button">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
                <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TCCompliancePanel;
